import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Checkbox, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
} from '@mui/material';
import axiosInstance from '../../../helpers/axios';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';


const inputStyle = {
  width: '100%', padding: '7px', border: '1px solid #ddd',
  borderRadius: '4px', fontSize: '12px', color: '#344054', boxSizing: 'border-box', marginTop: '2px',
};
const readOnlyStyle = { ...inputStyle, backgroundColor: '#fafafa', color: '#667085' };
const labelStyle = { fontSize: '10px', color: '#667085', display: 'block' };

export default function PaymentLinkModal({ open, loan, onClose }) {
  const [emis, setEmis] = useState([]);
  const [loadingEmis, setLoadingEmis] = useState(false);
  const [selectedEmiIds, setSelectedEmiIds] = useState([]);
  const [category, setCategory] = useState(2); // 2=Overdue, 1=Preclosure
  const [bounceDiscount, setBounceDiscount] = useState('0');
  const [penaltyDiscount, setPenaltyDiscount] = useState('0');
  const [remarks, setRemarks] = useState('');
  const [whatsapp, setWhatsapp] = useState(true);
  const [email, setEmail] = useState(false);
  const [sms, setSms] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open || !loan) return;
    setLoadingEmis(true);
    setSelectedEmiIds([]);
    setCategory(2);
    setBounceDiscount('0');
    setPenaltyDiscount('0');
    setRemarks('');

    axiosInstance
      .get(`/emis/overdue?loanId=${loan.loanId}`)
      .then((res) => {
        setEmis(res.data?.data || []);
      })
      .catch(() => setEmis([]))
      .finally(() => setLoadingEmis(false));
  }, [open, loan]);

  const selectedEmis = emis.filter((e) => selectedEmiIds.includes(e.emiId));
  const totalEmiAmt = selectedEmis.reduce((s, e) => s + (Number(e.emiAmount) || 0), 0);
  const totalBounce = selectedEmis.reduce((s, e) => s + (Number(e.bounceCharges) || 0), 0);
  const totalPenalty = selectedEmis.reduce((s, e) => s + (Number(e.penalty) || 0), 0);

  const bounceDiscNum = Math.min(Math.max(Number(bounceDiscount) || 0, 0), totalBounce);
  const penaltyDiscNum = Math.min(Math.max(Number(penaltyDiscount) || 0, 0), totalPenalty);
  const netAmount = Math.max(0, totalEmiAmt + totalBounce + totalPenalty - bounceDiscNum - penaltyDiscNum);

  const allSelected = emis.length > 0 && selectedEmiIds.length === emis.length;

  const handleToggleEmi = (emiId) => {
    setSelectedEmiIds((prev) =>
      prev.includes(emiId) ? prev.filter((id) => id !== emiId) : [...prev, emiId]
    );
  };

  const handleSelectAll = (e) => {
    setSelectedEmiIds(e.target.checked ? emis.map((em) => em.emiId) : []);
  };

  const handleGenerate = () => {
    if (selectedEmiIds.length === 0) {
      alert('Please select at least one EMI');
      return;
    }
    setGenerating(true);
    axiosInstance
      .post('/loan-repayment/payment-link', {
        loanId: loan.loanId,
        amount: netAmount,
        reason: category,
        emiIds: selectedEmiIds,
        penaltyDiscount: penaltyDiscNum,
        bounceDiscount: bounceDiscNum,
        whatsappNotification: whatsapp ? 1 : 0,
        emailNotification: email ? 1 : 0,
        remarks: remarks,
      })
      .then((res) => {
        if (res.data.message === 'Successful') {
          alert('Link generated — /loan-repayment/link/');
          onClose();
        }
      })
      .catch((err) => {
        alert(err?.response?.data?.message || 'Failed to generate payment link');
      })
      .finally(() => setGenerating(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '15px', fontFamily: 'Inter-Medium', fontWeight: 600, pb: 1.5, borderBottom: '1px solid #eee' }}>
        Payment Link
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loadingEmis ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
            <CircularProgress size={32} sx={{ color: '#D22129' }} />
          </Box>
        ) : (
          <>
            {/* ── EMI Selection Table ── */}
            <TableContainer component={Paper} elevation={0}
              sx={{ border: '1px solid #eee', borderRadius: '6px', mb: 2, maxHeight: 200, overflow: 'auto' }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fafbfc' }}>
                    <TableCell padding="checkbox" sx={{ backgroundColor: '#fafbfc' }}>
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={selectedEmiIds.length > 0 && !allSelected}
                        onChange={handleSelectAll}
                        sx={{ color: '#D22129', '&.Mui-checked': { color: '#D22129' }, '&.MuiCheckbox-indeterminate': { color: '#D22129' } }}
                      />
                    </TableCell>
                    {['EMI No', 'EMI Amount', 'Repymt Due On', 'Delay Charges', 'Ovd Days', 'Penalty', 'Bounce Charges', 'Total Ovd'].map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 600, color: '#667085', fontSize: '11px', whiteSpace: 'nowrap', backgroundColor: '#fafbfc' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emis.length > 0 ? emis.map((emi, idx) => {
                    const emiAmt = Number(emi.emiAmount) || 0;
                    const bounce = Number(emi.bounceCharges) || 0;
                    const penalty = Number(emi.penalty) || 0;
                    const ovdDays = Number(emi.overdueDays) || 0;
                    const total = Number(emi.overdueAmount) || (emiAmt + bounce + penalty);
                    const isSelected = selectedEmiIds.includes(emi.emiId);

                    return (
                      <TableRow
                        key={emi.emiId}
                        onClick={() => handleToggleEmi(emi.emiId)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#fafbfc' } }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => handleToggleEmi(emi.emiId)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ color: '#D22129', '&.Mui-checked': { color: '#D22129' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{emi.emiNo || idx + 1}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{emiAmt.toLocaleString('en-IN')}</TableCell>
                        <TableCell sx={{ fontSize: '12px', color: '#667085' }}>{fmtDate(emi.dueDate)}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{Number(emi.delayCharges) > 0 ? Number(emi.delayCharges).toLocaleString('en-IN') : '—'}</TableCell>
                        <TableCell sx={{ fontSize: '12px', color: ovdDays > 0 ? '#D22129' : '#9e9e9e' }}>
                          {ovdDays > 0 ? ovdDays : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{penalty > 0 ? penalty.toLocaleString('en-IN') : 0}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{bounce > 0 ? bounce.toLocaleString('en-IN') : 0}</TableCell>
                        <TableCell sx={{ fontSize: '12px', fontWeight: 700 }}>{total.toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 3, color: '#9e9e9e', fontSize: '12px' }}>
                        No pending EMIs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── Row 1: 4-column grid ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', mb: '12px' }}>
              <div>
                <label style={labelStyle}>Loan Id</label>
                <input value={loan?.legacyLoanId || loan?.loanId || ''} readOnly style={readOnlyStyle} />
              </div>
              <div>
                <label style={labelStyle}>Category</label>
                <select value={category} onChange={(e) => setCategory(Number(e.target.value))} style={inputStyle}>
                  <option value={2}>Overdue</option>
                  <option value={1}>Preclosure</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Total Remaining Emi Amount</label>
                <input value={totalEmiAmt} readOnly style={readOnlyStyle} />
              </div>
              <div>
                <label style={labelStyle}>Total Bounce Charges</label>
                <input value={totalBounce} readOnly style={readOnlyStyle} />
              </div>
            </Box>

            {/* ── Row 2: 4-column grid ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', mb: '12px' }}>
              <div>
                <label style={labelStyle}>Bounce Chrgs Discnt</label>
                <input
                  type="number" min={0} max={totalBounce}
                  value={bounceDiscount}
                  onChange={(e) => setBounceDiscount(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Total Penalty</label>
                <input value={totalPenalty} readOnly style={readOnlyStyle} />
              </div>
              <div>
                <label style={labelStyle}>Penalty Discount</label>
                <input
                  type="number" min={0} max={totalPenalty}
                  value={penaltyDiscount}
                  onChange={(e) => setPenaltyDiscount(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Net Amount</label>
                <input
                  value={netAmount.toLocaleString('en-IN')}
                  readOnly
                  style={{ ...readOnlyStyle, fontWeight: 700 }}
                />
              </div>
            </Box>

            {/* ── Row 3: Remarks + Delivery ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', alignItems: 'end' }}>
              <div>
                <label style={labelStyle}>Remarks</label>
                <input
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  style={inputStyle}
                  placeholder="Optional"
                />
              </div>
              <Box sx={{ display: 'flex', gap: '14px', pb: '7px' }}>
                {[{ label: 'SMS', state: sms, setState: setSms }, { label: 'WhatsApp', state: whatsapp, setState: setWhatsapp }, { label: 'Email', state: email, setState: setEmail }].map(({ label, state, setState }) => (
                  <label key={label} style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox" checked={state}
                      onChange={(ev) => setState(ev.target.checked)}
                      style={{ accentColor: '#D22129', cursor: 'pointer' }}
                    />
                    {label}
                  </label>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 2, pt: 1.5, borderTop: '1px solid #eee' }}>
        <Button
          onClick={handleGenerate}
          disabled={selectedEmiIds.length === 0 || generating}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: generating || selectedEmiIds.length === 0 ? '#cccccc' : '#D22129',
            color: 'white',
            '&:hover': { backgroundColor: '#b71c1c' },
            '&.Mui-disabled': { backgroundColor: '#cccccc', color: 'white' },
          }}
        >
          {generating ? 'Generating link...' : 'Generate Link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
